// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

// Mode développement - utilise des routes publiques
const DEV_MODE = false;

// Configuration des headers par défaut
const defaultHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

// Fonction pour obtenir le token d'authentification
const getAuthToken = () => {
  const storedToken = sessionStorage.getItem('fsf_auth_token') || localStorage.getItem('fsf_auth_token');
  console.log('getAuthToken - token récupéré:', storedToken ? `${storedToken.substring(0, 20)}...` : 'AUCUN');
  return storedToken;
};

// Fonction pour adapter les endpoints en mode développement
const getDevEndpoint = (endpoint) => {
  if (DEV_MODE) {
    if (endpoint.startsWith('/members')) {
      return endpoint.replace('/members', '/dev/members');
    }
    if (endpoint.startsWith('/contributions')) {
      return endpoint.replace('/contributions', '/dev/contributions');
    }
    if (endpoint.startsWith('/messages')) {
      return endpoint.replace('/messages', '/dev/messages');
    }
  }
  return endpoint;
};

// Fonction pour obtenir les headers avec authentification
const getAuthHeaders = () => {
  const token = getAuthToken();
  console.log('getAuthHeaders - token présent:', !!token);
  const headers = {
    ...defaultHeaders,
    ...(token && { Authorization: `Bearer ${token}` })
  };
  console.log('getAuthHeaders - headers:', headers);
  return headers;
};

// Classe pour gérer les appels API
class ApiService {
  // Méthode générique pour faire des requêtes
  async request(endpoint, options = {}) {
    const adaptedEndpoint = getDevEndpoint(endpoint);
    const url = `${API_BASE_URL}${adaptedEndpoint}`;
    const config = {
      headers: getAuthHeaders(),
      ...options,
    };

    console.log('ApiService.request - URL:', url);
    console.log('ApiService.request - config:', config);

    try {
      const response = await fetch(url, config);
      console.log('ApiService.request - statut réponse:', response.status);
      console.log('ApiService.renderquest - headers réponse:', Object.fromEntries(response.headers));

      const data = await response.json();
      console.log('ApiService.request - données reçues:', data);

      if (!response.ok) {
        console.error('ApiService.request - erreur HTTP:', response.status, data);

        // Créer une erreur avec plus d'informations pour les erreurs de validation
        const error = new Error(data.message || `Erreur HTTP: ${response.status}`);
        error.status = response.status;
        error.data = data;

        // Pour les erreurs 422 (validation), ajouter les détails des erreurs
        if (response.status === 422 && data.errors) {
          error.validationErrors = data.errors;
        }

        throw error;
      }

      return data;
    } catch (error) {
      console.error('ApiService.request - erreur:', error);
      throw error;
    }
  }

  // GET request
  get(endpoint, options = {}) {
    // Si des paramètres sont passés, les ajouter à l'URL
    let url = endpoint;
    if (options && options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          searchParams.append(key, value);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        url += (endpoint.includes('?') ? '&' : '?') + queryString;
      }
    }

    return this.request(url, {
      method: 'GET',
      ...(options || {})
    });
  }

  // POST request
  post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Upload de fichier
  upload(endpoint, formData) {
    const token = getAuthToken();
    const headers = {
      'Accept': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };

    // Vérifier si FormData contient _method pour override HTTP method
    const method = formData.has && formData.has('_method') ? 'POST' : 'POST';

    return this.request(endpoint, {
      method,
      headers,
      body: formData,
    });
  }

  // Téléchargement authentifié d'un fichier (retourne le blob et le nom de fichier)
  async downloadFile(endpoint) {
    const token = getAuthToken();
    const headers = { Accept: '*/*' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'GET', headers });

    if (!response.ok) {
      let message = `Erreur HTTP: ${response.status}`;
      try {
        const data = await response.json();
        message = data.message || message;
      } catch (e) {
        // réponse non JSON
      }
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }

    const disposition = response.headers.get('Content-Disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    return {
      blob: await response.blob(),
      fileName: match ? decodeURIComponent(match[1]) : null
    };
  }

  // === AUTHENTIFICATION ===
  async login(credentials) {
    console.log('ApiService.login - credentials:', credentials);
    console.log('ApiService.login - URL complète:', `${API_BASE_URL}/auth/login`);

    try {
      const data = await this.post('/auth/login', credentials);
      console.log('ApiService.login - réponse reçue:', data);

      if (data.token) {
        console.log('ApiService.login - token stocké:', data.token.substring(0, 20) + '...');
        sessionStorage.setItem('fsf_auth_token', data.token); localStorage.removeItem('fsf_auth_token');

        // Stocker les informations de redirection
        if (data.dashboard_type) {
          localStorage.setItem('dashboard_type', data.dashboard_type);
        }
        if (data.redirect_url) {
          localStorage.setItem('redirect_url', data.redirect_url);
        }
      }
      return data;
    } catch (error) {
      console.error('ApiService.login - erreur:', error);
      throw error;
    }
  }

  async register(userData) {
    const data = await this.post('/auth/register', userData);
    if (data.token) {
      sessionStorage.setItem('fsf_auth_token', data.token); localStorage.removeItem('fsf_auth_token');
    }
    return data;
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } finally {
      sessionStorage.removeItem('fsf_auth_token'); localStorage.removeItem('fsf_auth_token');
    }
  }

  async getUser() {
    return this.get('/auth/me');
  }

  async forgotPassword(email) {
    return this.post('/auth/forgot-password', { email });
  }

  // === MEMBRES ===
  async getMembers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/members?${queryString}` : '/members';
    return this.get(endpoint);
  }

  async getMember(id) {
    return this.get(`/members/${id}`);
  }

  async createMember(memberData) {
    return this.post('/members', memberData);
  }

  async updateMember(id, memberData) {
    return this.put(`/members/${id}`, memberData);
  }

  async deleteMember(id) {
    return this.delete(`/members/${id}`);
  }

  async getMembersStats() {
    return this.get('/members/stats');
  }

  // === COTISATIONS ===
  async getContributions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/contributions/admin?${queryString}` : '/contributions/admin';
    return this.get(endpoint);
  }

  async getContributionMembers() {
    return this.get('/contributions/members');
  }

  async recordPayment(paymentData) {
    return this.post('/contributions/payment', paymentData);
  }

  async getContributionsStats() {
    return this.get('/contributions/stats');
  }

  // === ÉVÉNEMENTS ===
  async getEvents(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/events?${queryString}` : '/events';
    return this.get(endpoint);
  }

  async getEvent(id) {
    return this.get(`/events/${id}`);
  }

  async createEvent(eventData) {
    return this.post('/events', eventData);
  }

  async updateEvent(id, eventData) {
    return this.put(`/events/${id}`, eventData);
  }

  async deleteEvent(id) {
    return this.delete(`/events/${id}`);
  }

  async getEventsStats() {
    return this.get('/events/stats');
  }

  // === CONTENU ===
  async getContents(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/contents?${queryString}` : '/contents';
    return this.get(endpoint);
  }

  async getContent(id) {
    return this.get(`/contents/${id}`);
  }

  async createContent(contentData) {
    return this.post('/contents', contentData);
  }

  async updateContent(id, contentData) {
    return this.put(`/contents/${id}`, contentData);
  }

  async deleteContent(id) {
    return this.delete(`/contents/${id}`);
  }

  async getContentsStats() {
    return this.get('/contents/stats');
  }

  // === GALERIE ===
  async getGalleryImages(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/gallery?${queryString}` : '/gallery';
    return this.get(endpoint);
  }

  async getGalleryImage(id) {
    return this.get(`/gallery/${id}`);
  }

  async uploadGalleryImage(formData) {
    return this.upload('/gallery', formData);
  }

  async updateGalleryImage(id, imageData) {
    return this.put(`/gallery/${id}`, imageData);
  }

  async deleteGalleryImage(id) {
    return this.delete(`/gallery/${id}`);
  }

  async getGalleryStats() {
    return this.get('/gallery/stats');
  }

  // === MAILING ===
  async getMessages(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/messages?${queryString}` : '/messages';
    return this.get(endpoint);
  }

  async getMessage(id) {
    return this.get(`/messages/${id}`);
  }

  async createMessage(messageData) {
    // Si c'est FormData, utiliser upload, sinon post normal
    if (messageData instanceof FormData) {
      return this.upload('/messages', messageData);
    }
    return this.post('/messages', messageData);
  }

  async updateMessage(id, messageData) {
    // Si c'est FormData, utiliser upload avec _method=PUT, sinon put normal
    if (messageData instanceof FormData) {
      // Laravel a besoin de _method=PUT pour les requêtes FormData
      messageData.append('_method', 'PUT');
      return this.upload(`/messages/${id}`, messageData);
    }
    return this.put(`/messages/${id}`, messageData);
  }

  async deleteMessage(id) {
    return this.delete(`/messages/${id}`);
  }

  async sendMessage(messageId, sendNow = false) {
    return this.post('/messages/send', {
      message_id: messageId,
      send_now: sendNow
    });
  }

  async getRecipientOptions() {
    return this.get('/messages/recipient-options');
  }

  async getMessagesStats() {
    return this.get('/messages/stats');
  }

  // === API PUBLIQUE ===
  async getPublicContents(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/public/contents?${queryString}` : '/public/contents';
    return this.get(endpoint);
  }

  async getPublicEvents(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/public/events?${queryString}` : '/public/events';
    return this.get(endpoint);
  }

  async getPublicGallery(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/public/gallery?${queryString}` : '/public/gallery';
    return this.get(endpoint);
  }

  // === NOUVELLES MÉTHODES POUR GESTION DES CONTRIBUTIONS ===

  // Mettre à jour le statut d'une cotisation
  async updateContributionStatus(contributionId, status) {
    const endpoint = `/contributions/${contributionId}/status`;
    console.log('🔄 Mise à jour statut - Endpoint:', endpoint);
    console.log('🔄 Données envoyées:', { status });
    return this.put(endpoint, { status });
  }

  // Supprimer une cotisation
  async deleteContribution(contributionId) {
    const endpoint = `/contributions/${contributionId}`;
    return this.delete(endpoint);
  }

  // === IMPORT/EXPORT MEMBRES ===

  // Exporter les membres vers Excel
  async exportMembers() {
    try {
      const token = getAuthToken();
      const headers = {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const response = await fetch(`${API_BASE_URL}/dev/members/export`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      // Retourner le blob pour téléchargement
      return await response.blob();
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
      throw error;
    }
  }

  // Importer les membres depuis Excel
  async importMembers(file) {
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);

      const headers = {
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const response = await fetch(`${API_BASE_URL}/dev/members/import`, {
        method: 'POST',
        headers,
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Erreur ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      throw error;

    }
  }

  // Télécharger le template Excel
  async downloadTemplate() {
    try {
      const token = getAuthToken();
      const headers = {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const response = await fetch(`${API_BASE_URL}/dev/members/template`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Erreur lors du téléchargement du template:', error);
      throw error;
    }
  }

  // Exporter les contributions du couple
  async exportCoupleContributions(coupleId, filters = {}) {
    try {
      const token = getAuthToken();
      const headers = {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ...(token && { Authorization: `Bearer ${token}` })
      };

      // Construire les paramètres de requête
      const queryParams = new URLSearchParams();
      if (filters.year) queryParams.append('year', filters.year);
      if (filters.month) queryParams.append('month', filters.month);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.search) queryParams.append('search', filters.search);

      const url = `${API_BASE_URL}/dev/contributions/couple/${encodeURIComponent(coupleId)}/export${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Erreur lors de l\'exportation des contributions du couple:', error);
      throw error;
    }
  }
}

// Instance singleton du service API
const apiService = new ApiService();

export default apiService;